import { useState, useEffect, Link, useNavigate, useSearchParams, motion, Button, Input, Card, CardContent, CardHeader, CardTitle, CardDescription, Label, Loader2, LOGO_URL, useAuth, toast } from "@/pages/shared";

export const AuthPage = ({ isLogin }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) { await login(email, password); }
      else { await register(name, email, password, referralCode); }
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      const templateSlug = searchParams.get("template");
      if (templateSlug) {
        navigate(`/dashboard/content?template=${templateSlug}`);
      } else {
        navigate("/dashboard");
      }
    } catch (error) { toast.error(error.response?.data?.detail || "Authentication failed"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 hero-glow">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <img src={LOGO_URL} alt="Creatara - Sign In to AI Content Generator" className="w-12 h-12 rounded-xl" />
          <span className="text-2xl font-bold">Creatara</span>
        </Link>
        <Card className="glass">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{isLogin ? "Welcome Back" : "Create Account"}</CardTitle>
            <CardDescription>{isLogin ? "Sign in to your account" : "Start your journey with Creatara"}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" className="mt-1 bg-white/5 border-white/10" required data-testid="name-input" /></div>
                  <div><Label>Referral Code (Optional)</Label><Input value={referralCode} onChange={(e) => setReferralCode(e.target.value)} placeholder="FRIEND123" className="mt-1 bg-white/5 border-white/10" data-testid="referral-input" /></div>
                </>
              )}
              <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1 bg-white/5 border-white/10" required data-testid="email-input" /></div>
              <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1 bg-white/5 border-white/10" required data-testid="password-input" /></div>
              <Button type="submit" className="w-full btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full" disabled={loading} data-testid="auth-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isLogin ? "Sign In" : "Create Account")}
              </Button>
            </form>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">Or continue with</span></div>
            </div>
            
            <Button onClick={loginWithGoogle} variant="outline" className="w-full rounded-full border-white/20 hover:bg-white/5">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Google
            </Button>
            
            <p className="text-center text-sm text-muted-foreground mt-6">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <Link to={isLogin ? "/register" : "/login"} className="text-violet-500 hover:underline">{isLogin ? "Sign up" : "Sign in"}</Link>
            </p>
            {isLogin && (
              <p className="text-center text-sm text-muted-foreground mt-2">
                <Link to="/forgot-password" className="text-violet-500 hover:underline" data-testid="forgot-password-link">Forgot your password?</Link>
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

